/**
 * Morrow website — single source of truth.
 * Every product fact here is treated as authoritative. Do not invent features,
 * numbers, customers, or integrations beyond what is stated.
 */

export const SITE = {
  name: 'Morrow',
  url: 'https://morrowproject.getaxiom.ca',
  ogImage: 'https://morrowproject.getaxiom.ca/og/morrow-og-card.jpg',
  tagline: 'Private intelligence, built around you.',
};

export const LINKS = {
  github: 'https://github.com/Mageester/morrow',
  changelog: 'https://github.com/Mageester/morrow/releases',
  installScript: 'https://morrowproject.getaxiom.ca/install.ps1',
  installScriptUnix: 'https://morrowproject.getaxiom.ca/install.sh',
};

export const INSTALL = {
  windows: 'iex (irm https://morrowproject.getaxiom.ca/install.ps1)',
  unix: 'curl -fsSL https://morrowproject.getaxiom.ca/install.sh | sh',
  terminal: 'morrow',
  onboard: 'morrow onboard',
  build: 'morrow build "<what you want>"',
  // Stated as it actually is. Windows installs a packaged build carrying its own
  // Node runtime; macOS and Linux install by building the verified release from
  // source, which is why they name their requirements rather than implying a
  // self-contained download that does not exist yet.
  platforms: [
    { name: 'Windows', detail: 'Windows 10/11 x64 · packaged build, nothing else needed', status: 'supported' as const },
    { name: 'macOS', detail: 'Apple silicon or Intel · checksum-verified package', status: 'supported' as const },
    { name: 'Linux', detail: 'x64 or arm64 · checksum-verified package', status: 'supported' as const },
  ],
};

export const NAV = [
  { label: 'Product', href: '/product' },
  { label: 'Cortex', href: '/cortex' },
  { label: 'Models', href: '/models' },
  { label: 'Docs', href: '/docs' },
];

export const FOOTER = {
  columns: [
    {
      title: 'Product',
      links: [
        { label: 'Product', href: '/product' },
        { label: 'Cortex', href: '/cortex' },
        { label: 'Models', href: '/models' },
        { label: 'Docs', href: '/docs' },
      ],
    },
    {
      title: 'Morrow',
      links: [
        { label: 'Install', href: '/install' },
        { label: 'Privacy', href: '/privacy' },
        { label: 'GitHub', href: LINKS.github, external: true },
        { label: 'Changelog', href: LINKS.changelog, external: true },
      ],
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  Home                                                               */
/* ------------------------------------------------------------------ */

export const HOME = {
  hero: {
    eyebrow: 'Morrow / local coding agent',
    title: 'A local coding agent you can verify.',
    body: 'Morrow reads your repository, plans changes, runs checks, and leaves an inspectable record. Your data stays local by default, and you choose the model.',
    cta: 'Install Morrow',
    ctaSecondary: 'See it in action',
    docs: 'Read the docs',
    platform: 'Windows 10/11 x64 · macOS x64/arm64 · Linux x64/arm64',
  },
  demo: {
    eyebrow: 'A verified workflow',
    heading: 'From intent to evidence.',
    body: 'Give Morrow an outcome. It turns the request into a plan, makes the change under the permissions you choose, and verifies what happened.',
  },
  workflow: {
    eyebrow: 'How it works',
    heading: 'Ask. Review. Verify.',
    body: 'The useful part is not only the answer. It is the trail that lets you understand and trust the work.',
    steps: [
      { number: '01', title: 'Ask', body: 'Describe the outcome in plain language, inside the project you are working on.' },
      { number: '02', title: 'Review', body: 'See the plan, affected scope, and permissions before Morrow changes anything.' },
      { number: '03', title: 'Verify', body: 'Inspect every read, run, and edit, then see the checks and evidence behind the result.' },
    ],
  },
  execution: {
    eyebrow: 'Visible execution',
    heading: 'See what it did.',
    body: 'Morrow keeps the work legible: the plan, the actions, the checks, and the evidence that supports the result.',
    command: 'morrow build "update the project"',
    samples: ['Read package.json', 'Planned the change', 'Ran the verification', 'Evidence available'],
    status: 'verified',
  },
  cortex: {
    eyebrow: 'Cortex',
    heading: 'Your project stays in context.',
    body: 'Cortex is Morrow\'s structured understanding of your repository — evidence-backed, local-first, and refreshed as the code changes.',
    items: ['architecture', 'conventions', 'decisions', 'risks'],
    cta: 'Explore Cortex',
  },
  models: {
    eyebrow: 'Models',
    heading: 'Use the model you choose.',
    body: 'Morrow is provider-neutral. Route a conversation to a hosted lab, a gateway, an OpenAI-compatible endpoint, or a local model.',
    route: 'you → Morrow → the model you choose',
    names: ['OpenAI', 'Anthropic', 'Google Gemini', 'OpenRouter', 'Ollama', 'llama.cpp'],
    cta: 'Explore models',
  },
  privacy: {
    eyebrow: 'Privacy boundary',
    heading: 'No outbound telemetry.',
    body: 'Morrow does not silently report what you do. Your projects, conversations, memory, execution records, and configuration stay local by default. External traffic is the model provider or tool activity you choose.',
    inside: ['projects', 'conversations', 'memory', 'execution records', 'configuration'],
    outside: ['the model provider you configure', 'external tools or network activity you permit'],
    cta: 'Read the privacy model',
  },
  final: {
    heading: 'Start with your own machine.',
    body: 'Self-hosted. Provider-neutral. Built around you.',
  },
};

/* ------------------------------------------------------------------ */
/*  Models                                                             */
/* ------------------------------------------------------------------ */

export interface Provider {
  name: string;
  note?: string;
}

export const MODEL_CATEGORIES: { id: string; name: string; blurb: string; items: Provider[] }[] = [
  {
    id: 'core',
    name: 'Core providers',
    blurb: 'First-class adapters for the major hosted labs and the OpenAI-compatible standard.',
    items: [
      { name: 'OpenAI' },
      { name: 'Anthropic' },
      { name: 'Google Gemini' },
      { name: 'OpenRouter' },
      { name: 'DeepSeek' },
      { name: 'OpenAI-compatible' },
      { name: 'Ollama', note: 'also a first-class local adapter' },
    ],
  },
  {
    id: 'gateways',
    name: 'Gateways',
    blurb: 'Aggregation layers that front many models through one endpoint.',
    items: [
      { name: 'OpenCode Zen' },
      { name: 'TokenRouter' },
      { name: 'Vercel AI Gateway' },
      { name: 'GitHub Models' },
    ],
  },
  {
    id: 'labs',
    name: 'Model labs',
    blurb: 'Direct routes to individual model builders.',
    items: [
      { name: 'xAI / Grok' },
      { name: 'Mistral AI' },
      { name: 'Moonshot AI / Kimi' },
      { name: 'Z.ai / GLM' },
      { name: 'Alibaba DashScope / Qwen' },
      { name: 'Perplexity' },
      { name: 'Cohere' },
    ],
  },
  {
    id: 'hosts',
    name: 'Inference hosts',
    blurb: 'Hosted inference platforms for fast, scalable model access.',
    items: [
      { name: 'Groq' },
      { name: 'Cerebras' },
      { name: 'Together AI' },
      { name: 'Fireworks AI' },
      { name: 'DeepInfra' },
      { name: 'Nebius AI Studio' },
      { name: 'Novita AI' },
      { name: 'Hyperbolic' },
      { name: 'NVIDIA NIM' },
      { name: 'SambaNova Cloud' },
    ],
  },
  {
    id: 'local',
    name: 'Local / self-hosted',
    blurb: 'Run inference on your own hardware. Local providers often need no credential.',
    items: [
      { name: 'Ollama' },
      { name: 'LM Studio' },
      { name: 'llama.cpp' },
      { name: 'vLLM' },
      { name: 'Jan' },
    ],
  },
];

export const MODEL_COUNT = 33;

export const AUTH_MODES = [
  {
    name: 'Subscription sign-in',
    detail: 'Anthropic / Claude and OpenAI / ChatGPT / Codex.',
    tone: 'accent' as const,
  },
  {
    name: 'Provider API keys',
    detail: 'Other hosted providers generally use provider API keys.',
    tone: 'default' as const,
  },
  {
    name: 'Local base URLs',
    detail: 'Local providers may use local base URLs and often need no credential.',
    tone: 'default' as const,
  },
];

/* ------------------------------------------------------------------ */
/*  Product                                                            */
/* ------------------------------------------------------------------ */

export const PRODUCT = {
  conversations: {
    heading: 'Persistent project conversations.',
    body: 'Sessions are stored locally rather than living only in terminal state. A closed terminal or interrupted UI does not erase the conversation — resume where you left off, in the same project context.',
  },
  chatBuild: {
    heading: 'Chat and Build.',
    body: 'Chat answers questions and reads the project without changing files. Build allows Morrow to make project changes. Supervision and automatic approval are a separate decision — not a different capability.',
  },
  execution: {
    heading: 'Visible execution.',
    body: 'Routine work collapses into compact summaries. Full detail — every read, run, and edit — lives in Activity / Inspect.',
    samples: ['Read package.json', 'Searched provider routing', 'Ran pnpm test', 'Edited chat-composer.tsx'],
  },
  terminal: {
    heading: 'A real terminal shell.',
    body: 'Compose multiline input, navigate with real cursor movement, reference files with @, and interrupt without losing the conversation.',
  },
  missions: {
    heading: 'Missions for durable work.',
    body: 'Long-running work with an objective, plan, milestones, and evidence. Completion states distinguish meaningful conditions instead of collapsing everything into \u201cdone\u201d.',
  },
  memory: {
    heading: 'Inspectable memory.',
    body: 'Automatic memory keeps durable preferences and project facts — not likely secrets, temporary noise, or raw transcripts. Everything stored can be inspected, edited, disabled, or deleted.',
  },
  skills: {
    heading: 'Skills, learned from evidence.',
    body: 'A learned workflow is trusted only after verified successful use — never because a model suggested it. Skills carry their permissions and network access with them.',
  },
  context: {
    heading: 'Context intelligence.',
    body: 'Morrow distinguishes what it knows about a model\u2019s context window from what it does not. Unknown limits stay unknown rather than becoming invented numbers.',
  },
  reasoning: {
    heading: 'Reasoning, as the route supports it.',
    body: 'No universal low / medium / high contract. Reasoning modes come from the route, and raw chain-of-thought is never stored in the persistent database.',
  },
};

/* ------------------------------------------------------------------ */
/*  Cortex                                                             */
/* ------------------------------------------------------------------ */

export const CORTEX = {
  hero: {
    heading: ['Your project shouldn\u2019t be', 'new every morning.'],
    body: 'Cortex is Morrow\u2019s structured understanding of your repository — evidence-backed, local-first, and refreshed as the code changes.',
  },
  story: [
    { title: 'The repository enters.', note: 'Morrow reads the project deterministically — files, commands, structure.' },
    { title: 'Morrow maps it.', note: 'Architecture, components, and entry points become a working model.' },
    { title: 'Knowledge appears.', note: 'Conventions, rules, decisions, and risks — each with evidence.' },
    { title: 'A file changes.', note: 'One edit, anywhere in the project.' },
    { title: 'Only affected knowledge becomes stale.', note: 'Not everything. Just what that file touches.' },
    { title: 'Morrow refreshes it.', note: 'The stale scope is re-verified; the rest stays current.' },
  ],
  tracks: ['architecture', 'components', 'commands', 'conventions', 'rules', 'decisions', 'risks', 'learnings'],
  freshness: {
    heading: 'Freshness, scoped precisely.',
    body: 'One changed file invalidates only the knowledge it touches — not the whole repository.',
    states: [
      { state: 'current', note: 'matches the codebase as last verified' },
      { state: 'possibly stale', note: 'relevant scope changed; unverified' },
      { state: 'stale', note: 'known to drift from the current code' },
      { state: 'invalidated', note: 'no longer trustworthy for this scope' },
    ],
  },
  impact: {
    heading: 'Impact analysis.',
    body: 'Before a change, Cortex identifies what is likely affected and what should be verified.',
  },
  planning: {
    heading: 'Adaptive planning.',
    body: 'When reality contradicts a plan, Morrow records bounded revisions instead of silently drifting.',
  },
  conventions: {
    heading: 'Conventions and rules.',
    body: 'Inferred conventions can be approved or rejected. Your explicit rules always outrank them.',
    example: 'Never modify generated files directly.',
  },
  skills: {
    heading: 'Learned workflows.',
    body: 'Successful safe workflows promote through an evidence-gated lifecycle — without silently gaining network or secret access.',
  },
};

/* ------------------------------------------------------------------ */
/*  Models page                                                        */
/* ------------------------------------------------------------------ */

export const MODELS = {
  hero: {
    heading: ['Use the intelligence', 'you choose.'],
  body: 'Morrow routes across 33 providers and routes spanning cloud labs, gateways, inference hosts, OpenAI-compatible endpoints, and local models. Morrow is not the model — you are never locked into one.',
  },
  neutrality: {
    heading: 'Morrow is not the model.',
    body: 'You talk to Morrow. Morrow talks to the model you choose — one per conversation, or swapped when the job changes.',
  },
  context: {
    heading: 'Context truth.',
    body: 'Native model context and provider limits are tracked separately. Unknown limits stay explicitly unknown — no invented fallbacks.',
  },
  reasoning: {
    heading: 'Reasoning controls.',
    body: 'Morrow normalizes user control while preserving what each provider actually supports — no universal low / medium / high assumption.',
  },
  availability: {
    heading: 'Honest availability.',
    body: 'Configured, reachable, verified, available — Morrow distinguishes these states rather than claiming a route works before it does.',
    states: ['configured', 'endpoint reachable', 'credential verified', 'model available', 'metadata known', 'metadata unknown'],
  },
  auth: {
    heading: 'Authentication.',
    body: 'Subscription sign-in for Anthropic and OpenAI. API keys for hosted providers. Local routes often need no credential.',
    modes: AUTH_MODES,
  },
  ecosystem: {
    heading: 'The ecosystem.',
    body: 'Every provider and route Morrow can talk to, searchable.',
  },
};

/* ------------------------------------------------------------------ */
/*  Privacy                                                            */
/* ------------------------------------------------------------------ */

export const PRIVACY = {
  hero: {
    heading: ['Your machine', 'is the home.'],
    body: 'Morrow binds its service to loopback by default. Local state lives beneath ~/.morrow/ with SQLite for durable storage.',
  },
  inside: ['projects', 'conversations', 'memory', 'Cortex intelligence', 'execution records', 'configuration'],
  outside: ['the model provider you configure', 'external tools or network activity you permit'],
  lead: 'No outbound telemetry. Morrow does not silently report what you do.',
  credentials: {
    heading: 'Provider credentials.',
    body: 'Credentials stay server-side — never in browser storage or browser-visible API payloads — and credential files use owner-restricted operating-system permissions.',
  },
  silent: {
    heading: 'No silent changes.',
    body: 'External provider fallback must not quietly change privacy behavior. Morrow exposes what provider and model are involved.',
  },
  boundaries: {
    heading: 'Memory boundaries.',
    body: 'Project memory stays project-scoped. Likely secrets are rejected from automatic capture, and you can inspect, edit, disable, export, or delete anything stored.',
  },
};

/* ------------------------------------------------------------------ */
/*  Install                                                            */
/* ------------------------------------------------------------------ */

export const INSTALL_PAGE = {
  heading: 'Install Morrow.',
  body: 'One command on Windows, macOS, or Linux. Each supported platform receives a checksum-verified release artifact when one is available.',
};
